import type { PortalOrderSummary, PortalOrdersResult } from '@erp/shared';
import { ShoppingBag } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Badge } from '../../components/shadcn/index.js';
import { Card, DataTable, type DataTableColumn, EmptyState, Skeleton } from '../../components/ui/index.js';
import { customerApi } from '../../lib/customerApi.js';
import { formatMoney } from '../../lib/money.js';

function statusVariant(status: string): 'success' | 'secondary' | 'warning' {
  if (status === 'fulfilled') return 'success';
  if (status === 'cancelled') return 'secondary';
  return 'warning';
}

/** The customer's online orders (submitted at the storefront / in the app). */
export function PortalOrdersPage(): JSX.Element {
  const { t, i18n } = useTranslation('portal');
  const [data, setData] = useState<PortalOrdersResult | null>(null);

  useEffect(() => {
    void customerApi()
      .get<PortalOrdersResult>('/orders', { params: { limit: 200 } })
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], total: 0 }));
  }, []);

  const columns: DataTableColumn<PortalOrderSummary>[] = useMemo(
    () => [
      {
        id: 'number',
        header: t('columns.number'),
        sortValue: (r) => r.orderNumber,
        searchValue: (r) => r.orderNumber,
        cell: (r) => (
          <Link to={`/portal/orders/${r.id}`} className="font-mono text-xs text-brand hover:underline">
            {r.orderNumber}
          </Link>
        ),
      },
      { id: 'date', header: t('columns.date'), sortValue: (r) => r.date, cell: (r) => r.date },
      {
        id: 'status',
        header: t('columns.status'),
        sortValue: (r) => r.status,
        cell: (r) => (
          <Badge variant={statusVariant(r.status)}>
            {t(`orders.status.${r.status}`, { defaultValue: r.status })}
          </Badge>
        ),
      },
      {
        id: 'total',
        header: t('columns.amount'),
        align: 'end',
        cellClassName: 'font-mono tabular-nums',
        sortValue: (r) => Number(r.total),
        cell: (r) => formatMoney(r.total, { locale: i18n.language, currency: r.currencyCode }),
      },
    ],
    [t, i18n.language],
  );

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-semibold">{t('orders.title', { defaultValue: 'My orders' })}</h1>
      {data === null ? (
        <Card className="flex flex-col gap-2 p-4">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </Card>
      ) : data.items.length === 0 ? (
        <EmptyState
          icon={<ShoppingBag className="h-8 w-8" aria-hidden />}
          title={t('orders.empty', { defaultValue: 'No orders yet' })}
          description={t('orders.emptyDescription', { defaultValue: 'Orders you place in the store will show up here.' })}
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable columns={columns} data={data.items} rowKey={(r) => r.id} searchPlaceholder={t('orders.searchPlaceholder', { defaultValue: 'Search orders…' })} />
        </Card>
      )}
    </div>
  );
}
